import { useLocale, useTranslation } from '@multaqa/i18n'
import { Badge } from '@multaqa/ui/components/ui/badge'
import { Button } from '@multaqa/ui/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@multaqa/ui/components/ui/dialog'
import { toast } from '@multaqa/ui/components/ui/sonner'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@multaqa/ui/components/ui/tabs'
import { useQuery } from '@tanstack/react-query'
import type { ReactNode } from 'react'
import { useState } from 'react'
import { AdminScreen } from '@/components/admin-screen'
import { CompanyDataImport } from '@/features/data-imports/company-data-import'
import { localizedText } from '@/lib/localized-text'
import { platformGroupsQuery } from '@/lib/platform'
import { isNotFound, useCompanyDetail, useSetCompanyStatus } from './api'
import { CompanyDepartments } from './company-departments'
import { CompanyDataError, CompanyNotFound } from './company-detail-states'
import { CompanyOverview } from './company-overview'
import { CompanyPositions } from './company-positions'
import { CompanySequences } from './company-sequences'
import { CompanySettings } from './company-settings'

export const COMPANY_DETAIL_TABS = [
  'overview',
  'settings',
  'departments',
  'positions',
  'sequences',
  'imports',
] as const
export type CompanyDetailTab = (typeof COMPANY_DETAIL_TABS)[number]

export function CompanyDetailScreen({
  groupId,
  companyId,
  tab = 'overview',
  onTabChange,
}: {
  groupId: string
  companyId: string
  tab?: CompanyDetailTab
  onTabChange?: (tab: CompanyDetailTab) => void
}) {
  const { t } = useTranslation()
  const { locale } = useLocale()
  const detail = useCompanyDetail(groupId, companyId)
  const groups = useQuery(platformGroupsQuery)
  const group = groups.data?.find((item) => item.id === groupId)
  const groupName = group ? localizedText(group.name, locale) : groupId

  if (detail.isError && isNotFound(detail.error)) {
    return <CompanyNotFound groupId={groupId} groupName={groupName} />
  }

  const company = detail.data
  const companyName = company ? localizedText(company.legal_name, locale) : groupName
  const testId = company
    ? 'company-detail'
    : detail.isLoading
      ? 'company-detail-loading'
      : 'company-detail-error'
  const trail: { label: string; href: string }[] = [
    { label: t('rail.admin.sections.customers'), href: '/groups' },
    { label: groupName, href: `/groups/${groupId}` },
  ]

  return (
    <AdminScreen
      // AdminScreen's `trail` type is `string[]`; PageHeader (what it forwards
      // to) accepts a linked crumb too, and the linked form is what this
      // screen has always shown.
      trail={trail as unknown as string[]}
      title={companyName}
      description={t('companies.subtitle')}
      actions={
        company ? (
          <CompanyHeaderMeta
            company={company}
            companyName={companyName}
            groupId={groupId}
            companyId={companyId}
          />
        ) : undefined
      }
      loading={detail.isLoading}
      testId={testId}
    >
      {company ? (
        <Tabs
          value={tab}
          onValueChange={(value) => {
            if (isCompanyDetailTab(value)) onTabChange?.(value)
          }}
          appearance="underline"
          className="min-h-0 flex-1 gap-0"
          data-testid="company-detail-tabs"
        >
          <TabsList className="overflow-x-auto px-4">
            {COMPANY_DETAIL_TABS.map((value) => (
              <TabsTrigger key={value} value={value} data-testid={`company-detail-tab-${value}`}>
                {t(`companies.tabs.${value}`)}
              </TabsTrigger>
            ))}
          </TabsList>
          <div className="min-h-0 flex-1 overflow-y-auto p-4">
            <LazyTab value="overview" active={tab === 'overview'}>
              <CompanyOverview groupId={groupId} company={company} />
            </LazyTab>
            <LazyTab value="settings" active={tab === 'settings'}>
              <CompanySettings groupId={groupId} companyId={companyId} />
            </LazyTab>
            <LazyTab value="departments" active={tab === 'departments'}>
              <CompanyDepartments groupId={groupId} companyId={companyId} />
            </LazyTab>
            <LazyTab value="positions" active={tab === 'positions'}>
              <CompanyPositions groupId={groupId} companyId={companyId} />
            </LazyTab>
            <LazyTab value="sequences" active={tab === 'sequences'}>
              <CompanySequences groupId={groupId} companyId={companyId} />
            </LazyTab>
            <LazyTab value="imports" active={tab === 'imports'}>
              <CompanyDataImport
                groupId={groupId}
                companyId={companyId}
                companyName={companyName}
              />
            </LazyTab>
          </div>
        </Tabs>
      ) : (
        <CompanyDataError retry={() => void detail.refetch()} />
      )}
    </AdminScreen>
  )
}

export function isCompanyDetailTab(value: string): value is CompanyDetailTab {
  return COMPANY_DETAIL_TABS.includes(value as CompanyDetailTab)
}

function CompanyHeaderMeta({
  company,
  companyName,
  groupId,
  companyId,
}: {
  company: { code: string | null; base_currency: string; status: string }
  companyName: string
  groupId: string
  companyId: string
}) {
  const { t } = useTranslation()
  const [confirming, setConfirming] = useState(false)
  const setStatus = useSetCompanyStatus()
  const status =
    company.status === 'active' || company.status === 'deactivated' ? company.status : null

  return (
    <div className="flex items-center gap-2">
      {company.code ? <span className="text-identifier font-mono">{company.code}</span> : null}
      <span className="text-identifier border-s border-border ps-2 font-mono">
        {company.base_currency}
      </span>
      {status ? (
        <Badge tone={status === 'active' ? 'success' : 'warning'}>
          {t(`companies.status.${status}`)}
        </Badge>
      ) : null}
      {status === 'active' ? (
        <Button variant="outline" size="sm" onClick={() => setConfirming(true)}>
          {t('companies.detail.deactivate')}
        </Button>
      ) : null}
      {status === 'deactivated' ? (
        <Button
          size="sm"
          loading={setStatus.isPending}
          disabled={setStatus.isPending}
          data-testid="company-detail-reactivate"
          onClick={() =>
            setStatus.mutate(
              { groupId, companyId, status: 'active' },
              {
                onSuccess: () => {
                  toast.success(t('companies.detail.reactivated', { name: companyName }))
                },
              },
            )
          }
        >
          {t('companies.detail.reactivate')}
        </Button>
      ) : null}
      {confirming ? (
        <DeactivateConfirm
          companyName={companyName}
          groupId={groupId}
          companyId={companyId}
          onClose={() => setConfirming(false)}
        />
      ) : null}
    </div>
  )
}

function DeactivateConfirm({
  companyName,
  groupId,
  companyId,
  onClose,
}: {
  companyName: string
  groupId: string
  companyId: string
  onClose: () => void
}) {
  const { t } = useTranslation()
  const setStatus = useSetCompanyStatus()

  return (
    <Dialog open onOpenChange={(open) => (!open ? onClose() : undefined)}>
      <DialogContent closeLabel={t('action.cancel')} data-testid="company-detail-deactivate-dialog">
        <DialogHeader>
          <DialogTitle>
            {t('companies.detail.deactivateDialog.title', { name: companyName })}
          </DialogTitle>
          <DialogDescription>
            {t('companies.detail.deactivateDialog.description', { name: companyName })}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter showCloseButton={false} closeLabel={t('action.cancel')}>
          <Button variant="outline" onClick={onClose} disabled={setStatus.isPending}>
            {t('action.cancel')}
          </Button>
          <Button
            variant="destructive"
            loading={setStatus.isPending}
            disabled={setStatus.isPending}
            data-testid="company-detail-deactivate-confirm"
            onClick={() =>
              setStatus.mutate(
                { groupId, companyId, status: 'deactivated' },
                {
                  onSuccess: () => {
                    toast.success(t('companies.detail.deactivated', { name: companyName }))
                    onClose()
                  },
                },
              )
            }
          >
            {t('companies.detail.deactivateDialog.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function LazyTab({
  value,
  active,
  children,
}: {
  value: CompanyDetailTab
  active: boolean
  children: ReactNode
}) {
  return <TabsContent value={value}>{active ? children : null}</TabsContent>
}
